<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Form } from '@/helpers/Form';
import ScriptsService, { FormModel, ScriptRoleEnum, ScriptTypeEnum } from '@/modules/low-code/services/ScriptsService';
import { Prop, Watch } from '@/helpers/Decorators';
import ActionButton from '@/components/action/ActionButton.vue';
import SourceScripts from '@/modules/low-code/views/administration/scripts/components/tabs/SourceScripts.vue';
import Documentation from '@/modules/low-code/views/administration/scripts/components/common/Documentation.vue';
import ProcessLogs from '@/modules/low-code/views/administration/scripts/components/tabs/ProcessLogs.vue';
import { KeyValue } from "@/helpers/Interfaces";
import { uniqueId } from 'lodash';
import Pager from '@/helpers/Pager';

@Options({
    components: {
        ActionButton,
        SourceScripts,
        Documentation,
        ProcessLogs
    },
    emits: ['scriptSaved', 'scriptClosed', 'cSharpModeChanged']
})
export default class ModuleForm extends Vue
{
    @Prop({ default: '' })
    public publicId: string;

    @Prop({ default: false })
    public isModalMode: boolean;

    @Prop({ default: false })
    public documentType: any;

    @Prop({ default: null })
    public moduleId: string;

    @Prop({ default: false })
    public scriptType: ScriptTypeEnum;

    private areDatasFromTemplateFetched = "";

    @Watch('form.template')
    public async onTemplateChanged(newValue: { key: string, value: string }): Promise<void>
    {
        if (newValue == null || !newValue.key)
        {
            if (!this.form.overrideBaseScript)
                this.form.baseScript = null;

            if (!this.form.overrideAdditionalScript)
                this.form.additionalScript = null;

            if (!this.form.overrideScriptConfig)
                this.form.scriptConfig = null;

            if (!this.form.overrideSharedScript)
                this.form.sharedScript = null;

            this.form.overrideBaseScript = false;
            this.form.overrideAdditionalScript = false;
            this.form.overrideScriptConfig = false;
            this.form.overrideSharedScript = false;

            return;
        }

        try
        {
            const result = (await ScriptsService.getDetails(newValue.key)).result;

            if (result)
            {
                if (!this.form.overrideBaseScript)
                    this.form.baseScript = result.baseScript;

                if (!this.form.overrideAdditionalScript)
                    this.form.additionalScript = result.additionalScript;

                if (!this.form.overrideScriptConfig)
                    this.form.scriptConfig = result.scriptConfig;

                if (!this.form.overrideSharedScript)
                    this.form.sharedScript = result.sharedScript;

                this.areDatasFromTemplateFetched = uniqueId();

                this.form.scriptEngine = result.scriptEngine;
            }
        }
        catch (ex)
        {
            this.$log.debug(ex);
        }
    }

    public form = Form.create<FormModel>({
        name: {},
        description: {},
        module: null,
        scriptRole: null,
        scriptType: null,
        isArchive: false,
        scriptEngine: null,
        scriptConfig: null,
        sharedScript: null,
        baseScript: null,
        additionalScript: null,
        linkedScripts: [],
        template: null,
        overrideAdditionalScript: false,
        overrideBaseScript: false,
        overrideScriptConfig: false,
        overrideSharedScript: false,
        isSystemScript: false,
        isGroupHeader: true,
        group: null,
        dependsOnGroups: [],
        namespace: '',
        filename: ''
    });

    public isInitialized: boolean = false;
    public tabIndex: number = 0;
    public scriptRoles: KeyValue<string, string>[] = [];
    public scriptTypes: KeyValue<string, string>[] = [];
    public scriptEngines: KeyValue<string, string>[] = [];
    public modulesReloadKey: number = 0;
    public permissions: any = null;
    public verifyDependenciesLoading: boolean = false;
    public verifyDependenciesErrors: string[] = [];

    public get isEditMode(): boolean
    {
        return !!this.publicId && this.publicId !== '0';
    }

    public get isSharedScript(): boolean
    {
        return this.form.scriptRole?.key == ScriptRoleEnum.SharedScript;
    }

    public get isTemplate(): boolean
    {
        return this.form.scriptRole?.key == ScriptRoleEnum.Template;
    }

    public get isClassLibrary(): boolean
    {
        return this.form.scriptRole?.key == ScriptRoleEnum.ClassLibrary;
    }

    public get isExecutableScript(): boolean
    {
        return this.form.scriptRole?.key == ScriptRoleEnum.ExecutableScript;
    }

    public get hasDraft(): boolean
    {
        return this.isClassLibrary && this.isEditMode && !!this.form.additionalScript;
    }

    public get hasTemplate(): boolean
    {
        return this.form.template != null;
    }

    public get isDebugMode(): boolean
    {
        return this.isEditMode && process.env.NODE_ENV !== "production";
    }

    public get isCsharpMode(): boolean
    {
        return this.isEditMode && (this.form.scriptEngine?.key ?? "") == 'CSharpEngine';
    }

    public get canExport(): boolean
    {
        return this.permissions?.export?.can ?? false;
    }

    public get canImport(): boolean
    {
        return this.permissions?.import?.can ?? false;
    }

    public get modulePager(): Pager
    {
        return new Pager(1, 999, '', 'ASC');
    }

    @Watch('isCsharpMode')
    public async onCsharpModeChanged(): Promise<void>
    {
        this.$emit('cSharpModeChanged', this.isCsharpMode);
    }

    public get scriptEnginesByType(): KeyValue<string, string>[]
    {
        const typeScriptTypes = [
            ScriptTypeEnum.DynamicClientSideTrigger.toString()
        ];

        const scriptEngines = typeScriptTypes.includes(this.form.scriptType?.key ?? '') ? this.scriptEngines.filter(p => p.key === 'TypeScript') : this.scriptEngines.filter(p => p.key !== 'TypeScript');

        if (!this.form.scriptType)
        {
            if (this.isSharedScript || this.isTemplate || this.isClassLibrary)
                return this.isClassLibrary ? this.scriptEngines.filter(p => p.key === 'CSharpEngine') : scriptEngines;
            else
                return [];
        }

        const clearScriptTypes = [
            ScriptTypeEnum.DynamicTableField.toString()
        ];

        const cSharpTypes = [
            ScriptTypeEnum.PrintTemplate.toString(),
            ScriptTypeEnum.DynamicModal.toString()
        ];

        if (clearScriptTypes.includes(this.form.scriptType?.key ?? ''))
            return scriptEngines.filter(p => p.key == 'ClearScript');

        if (cSharpTypes.includes(this.form.scriptType?.key ?? ''))
            return scriptEngines.filter(p => p.key == 'CSharpEngine');

        return scriptEngines;
    }

    public resetForm(): void
    {
        this.form = Form.create<FormModel>({
            name: this.form.name,
            description: this.form.description,
            module: this.form.module,
            scriptRole: this.form.scriptRole,
            scriptType: this.form.scriptType,
            isArchive: this.form.isArchive,
            scriptEngine: null,
            scriptConfig: null,
            sharedScript: null,
            baseScript: null,
            additionalScript: null,
            linkedScripts: [],
            template: null,
            overrideAdditionalScript: false,
            overrideBaseScript: false,
            overrideScriptConfig: false,
            overrideSharedScript: false,
            isSystemScript: this.form.isSystemScript,
            isGroupHeader: this.isClassLibrary ? (this.form.isGroupHeader ?? true) : true,
            group: this.isClassLibrary ? this.form.group : null,
            dependsOnGroups: this.isClassLibrary ? (this.form.dependsOnGroups ?? []) : [],
            filename: (this.isClassLibrary || this.isExecutableScript) ? (this.form.filename ?? '') : ''
        });
    }

    public async created(): Promise<void>
    {
        await this.form.ready([
            this.getScriptRoles(),
            this.getScriptTypes(),
            this.getScriptEngines(),
            this.getDetails(),
            this.getPermissions()
        ]);

        if (this.isModalMode)
            this.form.scriptRole = this.scriptRoles.find(p => p.key == ScriptRoleEnum.ExecutableScript);

        if (this.scriptType)
            this.form.scriptType = this.scriptTypes.find(p => p.key == this.scriptType);

        this.$events.$on('saveForm', (value: boolean) => this.saveForm(value));
        this.$events.$on('debugOn', (value: boolean) => this.debugOn(value));
        this.$events.$on('debugOff', (value: boolean) => this.debugOff(value));

        this.isInitialized = true;

        this.$nextTick(() =>
        {
            if (this.moduleId)
                this.modulesReloadKey++;
        });
    }

    public async getScriptRoles(): Promise<void>
    {
        try
        {
            const response = await ScriptsService.getDictionaryData('scriptRole');

            this.scriptRoles = response.items.map(p => ({key: p.result.key, value: p.result.value}));
        }
        catch (ex)
        {
            this.handleException(ex, {
                400: (ex: any) => this.$alert.warning(ex.message),
            });
        }
    }

    public async getScriptTypes(): Promise<void>
    {
        try
        {
            const response = await ScriptsService.getDictionaryData('scriptType');

            this.scriptTypes = response.items.map(p => ({key: p.result.key, value: p.result.value}));
        }
        catch (ex)
        {
            this.handleException(ex, {
                400: (ex: any) => this.$alert.warning(ex.message),
            });
        }
    }

    public async getScriptEngines(): Promise<void>
    {
        try
        {
            const response = await ScriptsService.getDictionaryData('scriptEngine');

            this.scriptEngines = response.items.map(p => ({key: p.result.key, value: p.result.value}));
        }
        catch (ex)
        {
            this.handleException(ex, {
                400: (ex: any) => this.$alert.warning(ex.message),
            });
        }
    }

    public async setDefaultScriptEngine(): Promise<void>
    {
        if (this.isEditMode) return;

        this.form.scriptEngine = this.scriptEnginesByType.find(p => p.key == 'CSharpEngine') ?? this.scriptEnginesByType.first();
    }

    public async getDetails(): Promise<any>
    {
        if (!this.isEditMode) return;

        try
        {
            const result = (await ScriptsService.getDetails(this.publicId)).result;

            this.form.withData(result);
        }
        catch (ex)
        {
            this.$log.debug(ex);
        }
    }

    public async getPermissions(): Promise<any>
    {
        if (!this.isEditMode) return;

        try
        {
            const result = (await ScriptsService.getPermissions(this.publicId));

            this.permissions = result;
        }
        catch (ex)
        {
            this.$log.debug(ex);
        }
    }

    public async debugOn(isExportMode: boolean)
    {
        try
        {
            this.form.wait();

            const response = await ScriptsService.debugOn(this.publicId, isExportMode);

            if (isExportMode)
            {
                const { file, scriptName } = response.data;
                const fileBytes = new Uint8Array(atob(file).split('').map(c => c.charCodeAt(0)));

                const blob = new Blob([fileBytes], { type: 'text/x-csharp' });

                this.triggerDownload(blob, scriptName);
            }
        }
        catch (ex)
        {
            this.$log.debug(ex);
        }
        finally
        {
            this.form.continue();
        }
    }

    public triggerDownload(blob: any, filename: string)
    {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    public async debugOff(isExportMode: boolean)
    {
        if (isExportMode)
        {
            this.$refs.scriptFile.click();

            return;
        }

        try
        {
            this.form.wait();

            const result = await ScriptsService.debugOff(this.publicId, isExportMode);

            this.SetDataFromScript(result);
        }
        catch (ex)
        {
            this.$log.debug(ex);
        }
        finally
        {
            this.form.continue();
        }
    }

    public SetDataFromScript(result: any): void
    {
        if (this.form.overrideBaseScript || !this.hasTemplate)
            this.form.baseScript = result.baseScript;

        if (this.form.overrideAdditionalScript  || !this.hasTemplate)
            this.form.additionalScript = result.additionalScript;

        if (this.form.overrideScriptConfig || !this.hasTemplate)
            this.form.scriptConfig = result.scriptConfig;

        if (this.form.overrideSharedScript || !this.hasTemplate)
            this.form.sharedScript = result.sharedScript;
    }

    public async handleFileChange(event: any) : Promise<void>
    {
        try
        {
            this.form.wait();

            const selectedFile = event.target.files[0];

            if (selectedFile == null || selectedFile == undefined)
            {
                this.form.continue();

                return;
            }

            const file = selectedFile;

            const formData = new FormData();

            formData.append("file", file);

            const result = await ScriptsService.importScriptFile(this.publicId, formData);

            this.SetDataFromScript(result);
        }
        catch (ex)
        {
            this.$log.debug(ex);
        }
        finally
        {
            this.form.continue();

            if (this.$refs.scriptFile)
                (this.$refs.scriptFile as HTMLInputElement).value = '';
        }
    }

    public async saveForm(newVersionMode: boolean): Promise<void>
    {
        try
        {
            this.form.wait();

            let scriptPublicId = this.publicId;

            const form = this.form.formatData();

            const codeErrors = this.$refs.configComponent.validateCode();

            if (codeErrors)
            {
                const err = new Error('Script validation error');

                (err as any).data = { errors: codeErrors };
                (err as any).code = 422;

                throw err;
            }

            this.scriptsToBase64(form);

            if (!this.isEditMode || newVersionMode)
            {
                const result = await ScriptsService.create(form, scriptPublicId);

                scriptPublicId = result.result.publicId;

                this.$alert.success(this.$t('[[[Skrypt został dodany.]]]'));
            }
            else
            {
                await ScriptsService.update(this.publicId, form);
                this.$alert.success(this.$t('[[[Skrypt został zaktualizowany.]]]'));
                if (this.isClassLibrary)
                    await this.getDetails();
            }

            this.verifyDependenciesErrors = [];
            ['scriptConfig', 'sharedScript', 'baseScript', 'additionalScript', 'scriptEngine'].forEach(f => this.form.$errors.clear(f));

            if (this.isModalMode)
                this.$emit('scriptSaved', form, scriptPublicId);
            else if (!this.isEditMode)
                this.redirect({ name: 'lowCode-scripts-edit', params: { publicId: scriptPublicId } });
        }
        catch (ex)
        {
            const errors = (ex as any).data?.errors ?? {};
            const hasBaseScriptErrors = errors.baseScript || errors.BaseScript;
            const tabIndex = errors && ((Object.keys(errors).length === 1 && errors.hasOwnProperty('scriptEngine')) || hasBaseScriptErrors) ? 1 : 0;

            this.handleException(ex, {
                400: (ex: any) => this.$alert.warning(ex.message),
                422: async (ex: any) =>
                {
                    this.form.$errors.record(ex.data?.errors ?? {});
                    this.tabIndex = tabIndex;
                    this.$nextTick(() => this.$scrollTo('.invalid-field'));
                    this.$nextTick(() =>
                        this.$alert.warning(this.$t('[[[Nie wszystkie pola zostały wypełnione prawidłowo.]]]'))
                    );
                    if (this.isClassLibrary && this.isEditMode && hasBaseScriptErrors)
                        await this.getDetails();
                },
            });
        }
        finally
        {
            this.form.continue();
        }
    }

    private scriptsToBase64(form: FormModel): void
    {
        const scriptProperties: Array<keyof FormModel> = ['scriptConfig', 'sharedScript', 'baseScript', 'additionalScript'];

        scriptProperties.forEach(property => this.encodeScriptProperty(form, property));
    }

    private encodeScriptProperty(form: FormModel, propertyName: keyof FormModel): void
    {
        if (this.form[propertyName] != null && this.form[propertyName] != '')
        {
            const utf8Encoded = new TextEncoder().encode(this.form[propertyName] as string);

            (form[propertyName] as string) = this.arrayBufferToBase64(utf8Encoded);
        }
    }

    private arrayBufferToBase64(buffer: Uint8Array): string
    {
        let binary = '';
        const bytes = new Uint8Array( buffer );
        const len = bytes.byteLength;

        for (let i = 0; i < len; i++)
        {
            binary += String.fromCharCode( bytes[ i ] );
        }

        return btoa( binary );
    }

    public unmounted(): void
    {
        this.$events.$off("saveForm");
        this.$events.$off("debugOn");
        this.$events.$off("debugOff");

        if (this.isModalMode)
            this.$emit('scriptClosed');
    }

    public updateFormField({ key, value }: { key: string, value: boolean })
    {
        if (key in this.form)
        {
            this.form[key] = value;
        }
        else
        {
            console.warn(`Key "${key}" does not exist in form.`);
        }
    }

    public onNavigateToScriptField(fieldKey: string): void
    {
        this.$nextTick(() =>
        {
            const target = document.querySelector('#script-field-' + fieldKey);
            if (target)
            {
                const accordionItem = target.closest('.accordion-item');
                const collapse = target.closest('.accordion-collapse');
                if (accordionItem && collapse && !collapse.classList.contains('show'))
                {
                    const btn = accordionItem.querySelector<HTMLButtonElement>('.accordion-button');
                    btn?.click();
                }

                this.$nextTick(() => this.$scrollTo('#script-field-' + fieldKey));
            }
        });
    }

    public changeScriptRole() : void
    {
        this.resetForm();
    }

    public onIsGroupHeaderChanged() : void
    {
        this.form.$errors.clear('isGroupHeader');
        if (this.form.isGroupHeader)
            this.form.group = null;
    }

    public async discardDraft(): Promise<void>
    {
        if (!this.publicId || !this.hasDraft) return;

        try
        {
            this.form.wait();
            await ScriptsService.discardDraft(this.publicId);
            this.$alert.success(this.$t('[[[Wersja robocza została wycofana.]]]'));
            await this.getDetails();
        }
        catch (ex)
        {
            this.$log.debug(ex);
            this.handleException(ex, {
                400: (ex: any) => this.$alert.warning(ex.response?.data ?? ex.message),
                404: () => this.$alert.warning(this.$t('[[[Skrypt nie został znaleziony.]]]'))
            });
        }
        finally
        {
            this.form.continue();
        }
    }

    public async verifyDependencies(): Promise<void>
    {
        if (!this.publicId || this.verifyDependenciesLoading) return;

        try
        {
            this.verifyDependenciesLoading = true;
            this.verifyDependenciesErrors = [];

            const result = await ScriptsService.verifyDependencies(this.publicId);

            this.verifyDependenciesErrors = (result.results || [])
                .filter(r => r.errors && r.errors.length > 0)
                .map(r => `[[[${r.scriptDisplayName || r.scriptName}]]]\n\n${JSON.stringify(r.errors)}`);

            if (this.verifyDependenciesErrors.length === 0)
                this.$alert.success(this.$t('[[[Wszystkie powiązane skrypty kompilują się poprawnie.]]]'));
        }
        catch (ex)
        {
            this.$log.debug(ex);
            this.handleException(ex, {
                400: (ex: any) => this.$alert.warning(ex.response?.data ?? ex.message),
                404: () => this.$alert.warning(this.$t('[[[Skrypt nie został znaleziony.]]]'))
            });
        }
        finally
        {
            this.verifyDependenciesLoading = false;
        }
    }
}
</script>

<template>
    <DataCard :use-panels="true" :overflow="true">
        <template v-if="!isModalMode" #toolbar>
            <ActionBar :toggle-footer="true">
                <BackButton toolbar :fallback="{ name:'lowCode-scripts' }" />
                <ActionButton
                    v-if="canExport"
                    variant="secondary"
                    form="register-data-sources"
                    icon="fa-solid fa-download"
                    :text="$t('[[[Eksportuj]]]')"
                    :disabled="!form.active()"
                    @click="debugOn(true)"
                />
                <ActionButton
                    v-if="canImport"
                    variant="secondary"
                    form="register-data-sources"
                    icon="fa-solid fa-upload"
                    :text="$t('[[[Importuj]]]')"
                    :disabled="!form.active()"
                    @click="debugOff(true)"
                />
                <template v-if="isDebugMode">
                    <ActionButton
                        variant="danger"
                        form="register-data-sources"
                        icon="fa-solid fa-download"
                        :text="$t('[[[DEBUG ON]]]')"
                        :disabled="!form.active()"
                        @click="debugOn(false)"
                    />
                    <ActionButton
                        variant="danger"
                        form="register-data-sources"
                        icon="fa-solid fa-upload"
                        :text="$t('[[[DEBUG OFF]]]')"
                        :disabled="!form.active()"
                        @click="debugOff(false)"
                    />
                </template>
                <ActionButton
                    variant="success"
                    form="register-data-sources"
                    icon="fas fa-check-circle"
                    :text="$t('[[[Zapisz]]]')"
                    :disabled="!form.active() || form.isSystemScript"
                    @click="saveForm(false)"
                />
                <ActionButton
                    v-if="hasDraft"
                    variant="outline-secondary"
                    form="register-data-sources"
                    icon="fa-solid fa-undo"
                    :text="$t('[[[Wycofaj wersję roboczą]]]')"
                    :disabled="form.loading()"
                    @click="discardDraft()"
                />
                <ActionButton
                    v-if="isClassLibrary && isEditMode"
                    variant="secondary"
                    form="register-data-sources"
                    icon="fa-solid fa-link"
                    :text="$t('[[[Weryfikuj powiązania]]]')"
                    :disabled="verifyDependenciesLoading"
                    :loading="verifyDependenciesLoading"
                    @click="verifyDependencies()"
                />
                <ActionButton
                    v-if="isEditMode"
                    variant="primary"
                    form="register-data-sources"
                    icon="fas fa-check-circle"
                    :text="$t('[[[Zapisz nową wersję]]]')"
                    :disabled="!form.active() || form.isSystemScript"
                    @click="saveForm(true)"
                />
            </ActionBar>
        </template>
        <template #default>
            <input v-show="false" ref="scriptFile" type="file" @change="handleFileChange" />
            <div v-if="form.isSystemScript" class="alert alert-warning">
                {{ $t('[[[Skrypt systemowy można modyfikować tylko poprzez migracje]]]') }}
            </div>
            <IdeoTabs v-model="tabIndex" content-class="mt-3 script-container" justified>
                <IdeoTab active>
                    <template #title>
                        <i class="fas fa-fw fa-gears"></i>
                        <span>{{ $t('[[[Ustawienia]]]') }}</span>
                    </template>
                    <template #default>
                        <div class="row">
                            <div class="col-md-6">
                                <form
                                    id="register-data-sources"
                                    @submit.prevent="saveForm(false)"
                                    @keydown="form.$errors.clear(($event.target as HTMLInputElement).name)"
                                >
                                    <fieldset :disabled="form.isSystemScript">
                                        <PlaceholderForm :loading="form.loading()">
                                            <IdeoFormLocalize v-slot="{ locale }">
                                                <IdeoFormGroup
                                                    :label="$t('[[[Nazwa]]]')"
                                                    :invalid-feedback="form.$errors.first('name')"
                                                    :state="form.$errors.state('name')"
                                                    required
                                                >
                                                    <IdeoFormInput v-model="form.name[locale]" type="text" name="name"></IdeoFormInput>
                                                </IdeoFormGroup>
                                            </IdeoFormLocalize>
                                            <IdeoFormLocalize v-slot="{ locale }">
                                                <IdeoFormGroup
                                                    :label="$t('[[[Opis]]]')"
                                                    :invalid-feedback="form.$errors.first('description')"
                                                    :state="form.$errors.state('description')"
                                                >
                                                    <IdeoFormTextarea v-model="form.description[locale]" type="text" name="description"></IdeoFormTextarea>
                                                </IdeoFormGroup>
                                            </IdeoFormLocalize>
                                            <IdeoFormGroup
                                                name="scriptRole"
                                                :label="$t('[[[Rola skryptu]]]')"
                                                :invalid-feedback="form.$errors.first('scriptRole')"
                                                :state="form.$errors.state('scriptRole')"
                                                required
                                            >
                                                <IdeoSelect
                                                    v-model="form.scriptRole"
                                                    :placeholder="$t('[[[Rola skryptu]]]')"
                                                    :options="scriptRoles"
                                                    :disabled="isEditMode || isModalMode"
                                                    @update:modelValue="() => { changeScriptRole(); form.$errors.clear('scriptRole'); setDefaultScriptEngine(); }"
                                                />
                                            </IdeoFormGroup>
                                            <template v-if="form.scriptRole">
                                                <IdeoFormGroup
                                                    v-if="isClassLibrary"
                                                    :invalid-feedback="form.$errors.first('isGroupHeader')"
                                                    :state="form.$errors.state('isGroupHeader')"
                                                >
                                                    <IdeoFormCheckbox v-model="form.isGroupHeader" :disabled="isEditMode" @update:modelValue="onIsGroupHeaderChanged">
                                                        {{ $t('[[[Skrypt nagłówkowy grupy]]]') }}
                                                    </IdeoFormCheckbox>
                                                </IdeoFormGroup>
                                                <IdeoFormGroup
                                                    v-if="isClassLibrary && !form.isGroupHeader"
                                                    name="group"
                                                    :label="$t('[[[Grupa]]]')"
                                                    :invalid-feedback="form.$errors.first('group')"
                                                    :state="form.$errors.state('group')"
                                                    required
                                                >
                                                    <IdeoSelect
                                                        v-model="form.group"
                                                        :disabled="form.isSystemScript"
                                                        :placeholder="$t('[[[Grupa]]]')"
                                                        :endpoint="`simple-dictionary/scripts/form/group`"
                                                        @update:modelValue="form.$errors.clear('group');"
                                                    />
                                                </IdeoFormGroup>
                                                <IdeoFormGroup
                                                    v-if="isClassLibrary && form.isGroupHeader"
                                                    name="dependsOnGroups"
                                                    :label="$t('[[[Powiązane grupy]]]')"
                                                    :invalid-feedback="form.$errors.first('dependsOnGroups')"
                                                    :state="form.$errors.state('dependsOnGroups')"
                                                >
                                                    <IdeoSelect
                                                        v-model="form.dependsOnGroups"
                                                        :disabled="form.isSystemScript"
                                                        :placeholder="$t('[[[Powiązane grupy]]]')"
                                                        :endpoint="isEditMode && publicId ? `simple-dictionary/scripts/form/${publicId}/dependsOnGroups` : `simple-dictionary/scripts/form/dependsOnGroups`"
                                                        :multiselect="true"
                                                        @update:modelValue="form.$errors.clear('dependsOnGroups');"
                                                    />
                                                </IdeoFormGroup>
                                                <IdeoFormGroup
                                                    v-if="isClassLibrary || isExecutableScript"
                                                    name="filename"
                                                    :label="$t('[[[Nazwa pliku]]]')"
                                                    :invalid-feedback="form.$errors.first('filename')"
                                                    :state="form.$errors.state('filename')"
                                                >
                                                    <IdeoFormInput v-model="form.filename" type="text" name="filename" :placeholder="$t('[[[Opcjonalnie – domyślnie PublicId.cs]]]')"></IdeoFormInput>
                                                </IdeoFormGroup>
                                                <IdeoFormGroup
                                                    v-if="!isClassLibrary"
                                                    name="scriptType"
                                                    :label="$t('[[[Typ skryptu]]]')"
                                                    :invalid-feedback="form.$errors.first('scriptType')"
                                                    :state="form.$errors.state('scriptType')"
                                                    :required="form.scriptRole?.key == 'ExecutableScript'"
                                                >
                                                    <IdeoSelect
                                                        v-model="form.scriptType"
                                                        :placeholder="$t('[[[Typ skryptu]]]')"
                                                        :options="scriptTypes"
                                                        :disabled="isEditMode || isModalMode"
                                                        @update:modelValue="() => { form.$errors.clear('scriptType'); setDefaultScriptEngine(); }"
                                                    />
                                                </IdeoFormGroup>
                                                <IdeoFormGroup
                                                    v-if="!isClassLibrary"
                                                    name="module"
                                                    :label="$t('[[[Typ dokumentu]]]')"
                                                    :invalid-feedback="form.$errors.first('module')"
                                                    :state="form.$errors.state('module')"
                                                >
                                                    <IdeoSelect
                                                        v-model="form.module"
                                                        :placeholder="$t('[[[Typ dokumentu]]]')"
                                                        :endpoint="`simple-dictionary/scripts/form/module`"
                                                        :disabled="isEditMode || isModalMode"
                                                        :reload-key="modulesReloadKey"
                                                        :reload-value="moduleId"
                                                        :reload-option-key="'publicId'"
                                                        :pager="modulePager"
                                                    />
                                                </IdeoFormGroup>
                                                <IdeoFormGroup
                                                    v-if="!isSharedScript && !isTemplate && !isClassLibrary"
                                                    name="template"
                                                    :label="$t('[[[Szablon]]]')"
                                                    :invalid-feedback="form.$errors.first('template')"
                                                    :state="form.$errors.state('template')"
                                                >
                                                    <IdeoSelect
                                                        v-model="form.template"
                                                        :disabled="form.isSystemScript"
                                                        :placeholder="$t('[[[Szablon]]]')"
                                                        :endpoint="`simple-dictionary/scripts/form/template`"
                                                        :narrow-options="{
                                                            scriptType: form.scriptType?.key,
                                                            moduleId: form.module?.key
                                                        }"
                                                        @update:modelValue="form.$errors.clear('template');"
                                                    />
                                                </IdeoFormGroup>
                                                <IdeoFormGroup
                                                    v-if="!isSharedScript && !isClassLibrary"
                                                    name="linkedScripts"
                                                    :label="$t('[[[Skrypty współdzielone]]]')"
                                                    :invalid-feedback="form.$errors.first('linkedScripts')"
                                                    :state="form.$errors.state('linkedScripts')"
                                                >
                                                    <IdeoSelect
                                                        v-model="form.linkedScripts"
                                                        :disabled="form.isSystemScript"
                                                        :placeholder="$t('[[[Skrypty współdzielone]]]')"
                                                        :endpoint="`simple-dictionary/scripts/form/linkedScripts`"
                                                        :multiselect="true"
                                                        :narrow-options="{
                                                            scriptType: form.scriptType?.key,
                                                            moduleId: form.module?.key
                                                        }"
                                                        @update:modelValue="form.$errors.clear('linkedScripts');"
                                                    />
                                                </IdeoFormGroup>
                                                <IdeoFormGroup
                                                    :invalid-feedback="form.$errors.first('isArchive')"
                                                    :state="form.$errors.state('isArchive')"
                                                >
                                                    <IdeoFormCheckbox v-model="form.isArchive">
                                                        {{ $t('[[[Skrypt archiwalny]]]') }}
                                                    </IdeoFormCheckbox>
                                                </IdeoFormGroup>
                                            </template>
                                        </PlaceholderForm>
                                    </fieldset>
                                </form>
                            </div>
                        </div>
                    </template>
                </IdeoTab>
                <IdeoTab>
                    <template #title>
                        <i class="fas fa-fw fa-diagram-subtask"></i>
                        <span>{{ $t('[[[Skrypty]]]') }}</span>
                    </template>
                    <template #default>
                        <SourceScripts
                            v-if="isInitialized"
                            ref="configComponent"
                            :form="form"
                            :script-engines="scriptEnginesByType"
                            :template-data-uuid="areDatasFromTemplateFetched"
                            :public-id="publicId"
                            :has-draft="hasDraft"
                            :verify-dependencies-errors="verifyDependenciesErrors"
                            @saveForm="saveForm(false)"
                            @update-form-field="updateFormField"
                            @navigate-to-field="onNavigateToScriptField"
                        />
                    </template>
                </IdeoTab>
                <IdeoTab v-if="isEditMode">
                    <template #title>
                        <i class="fas fa-fw fa-exclamation-triangle"></i>
                        <span>{{ $t('[[[Logi błędów]]]') }}</span>
                    </template>
                    <template #default>
                        <ProcessLogs :public-id="publicId" />
                    </template>
                </IdeoTab>
            </IdeoTabs>
        </template>
        <template #right>
            <Documentation :form="form" />
        </template>
    </DataCard>
</template>
<style lang="scss">
.script-container{
    overflow-y: auto;
    overflow-x: hidden;
    max-height: calc(100vh - 260px);
}
</style>
